import React, { useState, useEffect, useCallback } from "react";
import UIButton from "../../components/ui-button";
import ShieldCheck from "../../assets/sell/shield-check.svg";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import FunnelStepTypes from "../funnel-step/types";
import { useDispatch } from "react-redux";
import Loader from "../../components/loader";
import { trackEvents } from "../../utils/tracking-events";
import {
  getPortalCD,
  readCookie,
  createCookie,
  deleteCookie,
} from "../../utils/helpers";
import {
  convertToLowerCase,
  getCountryCode,
} from "../../utils/utility-functions";
import { isValidGuid } from "../../utils/validation";
import {
  PERSONALITY_TEST_ASSESSMENT_ID,
  BIG_FIVE_TEST_ASSESSMENT_ID,
  MODEL_TYPE_ENNEAGRAM,
  ENNEAGRAM_TEST_ASSESSMENT_ID,
  LIKEABILITY_TEST_ASSESSMENT_ID,
  DISC_TEST_ASSESSMENT_ID,
  PERSONALITY_DISORDER_TEST_ASSESSMENT_ID,
  CAREER_PREFERENCE_TEST_ASSESSMENT_ID,
  ADHD_TEST_ASSESSMENT_ID,
  EMOTIONAL_INTELLIGENCE_TEST_ASSESSMENT_ID,
  DIFFICULT_PERSON_TEST_ASSESSMENT_ID,
  AUTISM_SPECTRUM_TEST_ASSESSMENT_ID,
  HIGHLY_SENSITIVE_PERSON_TEST_ASSESSMENT_ID,
  DYSLEXIA_SCREENING_CHILDREN_TEST_ASSESSMENT_ID,
  LOVE_STYLE_TEST_ASSESSMENT_ID,
  NARCISSISTIC_PERSONALITY_DISORDER_ASSESSMENT_ID,
  ANTISOCIAL_PERSONALITY_DISORDER_ASSESSMENT_ID,
  COUNTRY_CODE,
  DYSLEXIA_SCREENING_ADULT_TEST_ASSESSMENT_ID,
  ATTACHMENT_STYLE_TEST_ASSESSMENT_ID,
  CAREER_TEST_ASSESSMENT_ID,
  FOUR_TEMPERAMENTS_PERSONALITY_TEST_ASSESSMENT_ID,
  ROMANTIC_COMPATIBILITY_ASSESSMENT_ID,
  COLOR_BLINDNESS_TEST_ASSESSMENT_ID,
  DEPRESSION_TEST_ASSESSMENT_ID,
  BIPOLAR_DISORDER_ASSESSMENT_ID,
  FALSE_PLAN_MAX_USER_COUNT,
  PSYCHOPATH_ASSESSMENT_ID,
  CHILDHOOD_TRAUMA_TEST_ASSESSMENT_ID,
  SOCIAL_ANXIETY_TEST_ASSESMENT_ID,
  AUTISM_CHILDREN_TEST_ASSESSMENT_ID,
  SOCIOPATH_TEST_ASSESSMENT_ID,
} from "../../utils/general-config";
import { useTranslation } from "react-i18next";

import { APP_NAVIGATION, LOCALE } from "../../utils/constants";
import PathHelper from "../../utils/path-helper";
import useSignOut from "../../utils/sign-out";

import {
  ASSESSMENT_PLAN_CONFIG,
  generatePlanFeatures,
  getAssessmentPlanType,
} from "../../utils/plan-page-feature-utils";
import HeaderV2 from "../../components/header-v2";
import IqBellCurvedCard from "../../components/iq-bell-curved-card";
import Footer_V2 from "../../components/footer-v2/component";

const PERKS = [
  {
    icon: "📄",
    colorClass: "bg-perk-blue",
    titleKey: "plan_v2.perk_report_title",
    descKey: "plan_v2.perk_report_desc",
  },
  {
    icon: "🧪",
    colorClass: "bg-perk-purple",
    titleKey: "plan_v2.perk_tests_title",
    descKey: "plan_v2.perk_tests_desc",
  },
  {
    icon: "👥",
    colorClass: "bg-perk-orange",
    titleKey: null,
    descKey: "plan_v2.perk_invite_desc",
  },
  {
    icon: "🔗",
    colorClass: "bg-perk-teal",
    titleKey: "plan_v2.perk_share_title",
    descKey: "plan_v2.perk_share_desc",
  },
  {
    icon: "🏆",
    colorClass: "bg-perk-yellow",
    titleKey: "plan_v2.perk_achievements_title",
    descKey: "plan_v2.perk_achievements_desc",
  },
];

const PushAssessmentAttemptIdToFunnelStep = (assessmentAttemptId) => ({
  type: FunnelStepTypes.SET_ASSESSMENT_ATTEMPT_ID,
  assessmentAttemptId,
});

const PushAssessmentIdToFunnelStep = (assessmentId) => ({
  type: FunnelStepTypes.SET_ASSESSMENT_ID,
  assessmentId,
});

const Plan = ({
  getAssessmentResultByAssessmentAttemptIdConnect,
  getPlansConnect,
  funnelStep,
  getAssessmentAttemptByUserIdConnect,
  GetUserByIdConnect,
  IsGuestOrPremiumUserConnect,
  getAbTestConnect,
  getInsightfulContentConnect,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const signOut = useSignOut();
  // const token = getCookie("jwtToken");
  const [isEnneagramContentLoading, setIsEnneagramContentLoading] =
    useState(false);
  const {
    selectedPlan,
    userGUID,
    email,
    gender,
    assessmentAttemptId: attemptId,
    countryCD: reduxCountryCD,
    currencyCD,
    assessmentId,
    isAuth,
  } = funnelStep || {};
  const countryCD = getCountryCode(reduxCountryCD);
  const country = countryCD;
  const isVisibleInUSUKCanada =
    country === COUNTRY_CODE.USA ||
    country === COUNTRY_CODE.CANADA ||
    country === COUNTRY_CODE.UK;

  const queryParams = new URLSearchParams(location.search);
  // Extracting the language code from the URL path
  const { locale } = useParams();
  const currentLocale = locale ?? LOCALE.EN;
  let assessmentAttemptId =
    isValidGuid(queryParams.get("assessmentAttemptId")) &&
    queryParams.get("assessmentAttemptId");
  const [userAssessmentResult, setUserAssessmentResult] = useState(null);
  const loginPath = PathHelper(APP_NAVIGATION.SINI, currentLocale);

  const checkoutPath = PathHelper(APP_NAVIGATION.CHECKOUTIQ, currentLocale);
  // const checkoutCPath = PathHelper(APP_NAVIGATION.CHECKOUT_C, currentLocale);
  // const dashboardAbTestValue = getCookie("dashboardAbTestValue");
  const dashboardPath = PathHelper(APP_NAVIGATION.DASHBOARD_B, currentLocale);

  const [planData, setPlanData] = useState([]);
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);

  const { t } = useTranslation();

  // Reusable function to handle first plan cookie logic
  const handleFalsePlanCookie = useCallback(
    (planId) => {
      if (
        planData &&
        planData[0] &&
        convertToLowerCase(planId) === convertToLowerCase(planData[0].id)
      ) {
        createCookie("isFalsePlan", "true", 1);
      } else {
        deleteCookie("isFalsePlan");
      }
    },
    [planData],
  );

  const PushSelectedPlanToFunnelStep = (selectedPlan) => ({
    type: FunnelStepTypes.SET_SELECTED_PLAN,
    selectedPlan,
  });

  useEffect(() => {
    if (!isAuth) {
      navigate(loginPath.next);
    }
  }, [isAuth]);

  useEffect(() => {
    if (userGUID) {
      IsGuestOrPremiumUserConnect(userGUID).then((res) => {
        if (res && res?.isPremiumOrGuestUser) {
          navigate(dashboardPath.next);
        }
      });
    }
  }, [userGUID]);

  useEffect(() => {
    setIsLoading(true);
    trackEvents("planView", {
      userGuid: userGUID,
      email: email,
      eventLabel: "Page View",
      assessmentAttemptId: assessmentAttemptId,
      assessmentId: assessmentId,
      countryCD,
      currencyCD,
      portalCD: getPortalCD(currentLocale),
      gender: gender,
      visitId: readCookie("visitGuid"),
      visitorId: readCookie("visitorGuid"),
    });
  }, []);

  const validAssessmentIds = new Set([
    PERSONALITY_TEST_ASSESSMENT_ID,
    BIG_FIVE_TEST_ASSESSMENT_ID,
    ENNEAGRAM_TEST_ASSESSMENT_ID,
    LIKEABILITY_TEST_ASSESSMENT_ID,
    DISC_TEST_ASSESSMENT_ID,
    PERSONALITY_DISORDER_TEST_ASSESSMENT_ID,
    CAREER_PREFERENCE_TEST_ASSESSMENT_ID,
    ADHD_TEST_ASSESSMENT_ID,
    EMOTIONAL_INTELLIGENCE_TEST_ASSESSMENT_ID,
    DIFFICULT_PERSON_TEST_ASSESSMENT_ID,
    AUTISM_SPECTRUM_TEST_ASSESSMENT_ID,
    HIGHLY_SENSITIVE_PERSON_TEST_ASSESSMENT_ID,
    DYSLEXIA_SCREENING_CHILDREN_TEST_ASSESSMENT_ID,
    LOVE_STYLE_TEST_ASSESSMENT_ID,
    NARCISSISTIC_PERSONALITY_DISORDER_ASSESSMENT_ID,
    ANTISOCIAL_PERSONALITY_DISORDER_ASSESSMENT_ID,
    ATTACHMENT_STYLE_TEST_ASSESSMENT_ID,
    DYSLEXIA_SCREENING_ADULT_TEST_ASSESSMENT_ID,
    CAREER_TEST_ASSESSMENT_ID,
    FOUR_TEMPERAMENTS_PERSONALITY_TEST_ASSESSMENT_ID,
    ROMANTIC_COMPATIBILITY_ASSESSMENT_ID,
    COLOR_BLINDNESS_TEST_ASSESSMENT_ID,
    DEPRESSION_TEST_ASSESSMENT_ID,
    BIPOLAR_DISORDER_ASSESSMENT_ID,
    PSYCHOPATH_ASSESSMENT_ID,
    CHILDHOOD_TRAUMA_TEST_ASSESSMENT_ID,
    AUTISM_CHILDREN_TEST_ASSESSMENT_ID,
    SOCIOPATH_TEST_ASSESSMENT_ID,
  ]);

  useEffect(() => {
    if (userGUID) {
      GetUserByIdConnect(userGUID).then((res) => {
        if (res) {
          if (res.isValidEmail === false) {
            signOut({ isNavigate: true });
            return;
          }
        }
      });
    }
  }, [userGUID]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);

        if (!assessmentAttemptId) {
          const result = await getAssessmentAttemptByUserIdConnect(
            userGUID,
            currentLocale,
          );

          if (result?.length > 0) {
            const completedAssessmentResult = result.filter(
              (item) =>
                validAssessmentIds.has(item.assessmentId) &&
                item.personality !== null,
            );
            const latestAttempt = completedAssessmentResult.sort(
              (a, b) => new Date(b.createdOn) - new Date(a.createdOn),
            )[0];

            // const latestAssessmentAttemptId = result
            //   ?.sort((a, b) => new Date(b.createdOn) - new Date(a.createdOn))
            //   ?.map((item, index) =>
            //     index === 0 ? item.assessmentAttemptId : null
            //   )
            //   ?.filter(Boolean)[0];

            assessmentAttemptId = latestAttempt?.assessmentAttemptId;

            // assessmentAttemptId = latestAssessmentAttemptId;

            const currentUrl = new URL(window.location.href);
            if (window.location.href.includes("assessmentAttemptId=")) {
              currentUrl.searchParams.delete("assessmentAttemptId");
              currentUrl.searchParams.set(
                "assessmentAttemptId",
                assessmentAttemptId,
              );
              window.history.pushState({}, "", currentUrl);
            }

            dispatch(
              PushAssessmentAttemptIdToFunnelStep(
                latestAttempt?.assessmentAttemptId,
              ),
            );
            dispatch(PushAssessmentIdToFunnelStep(latestAttempt?.assessmentId));
          }
        }

        if (assessmentAttemptId) {
          const result = await getAssessmentResultByAssessmentAttemptIdConnect(
            assessmentAttemptId,
            currentLocale,
          );

          setUserAssessmentResult(result);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false); // Ensure loading is stopped
      }
    };

    fetchData();
  }, [assessmentAttemptId, getAssessmentResultByAssessmentAttemptIdConnect]);

  const personalityGroup = userAssessmentResult
    ? userAssessmentResult?.personality?.role
    : "";

  const personalityType = userAssessmentResult
    ? userAssessmentResult?.personality?.title
    : "";

  const personalityName = userAssessmentResult
    ? userAssessmentResult?.personality?.name
    : "";

  const personalityNameEnglishCode = userAssessmentResult
    ? userAssessmentResult?.personality?.nameEnglishCode
    : "";

  const ENNEAGRAM_TEST =
    !!userAssessmentResult &&
    userAssessmentResult?.assessmentId === ENNEAGRAM_TEST_ASSESSMENT_ID;

  const isCareerPreferenceTest =
    !!assessmentId &&
    convertToLowerCase(assessmentId) ===
      convertToLowerCase(CAREER_PREFERENCE_TEST_ASSESSMENT_ID);

  const isRomanticCompatibilityTest =
    !!assessmentId &&
    convertToLowerCase(assessmentId) ===
      convertToLowerCase(ROMANTIC_COMPATIBILITY_ASSESSMENT_ID);

  const isColorBlindTest =
    !!assessmentId &&
    convertToLowerCase(assessmentId) ===
      convertToLowerCase(COLOR_BLINDNESS_TEST_ASSESSMENT_ID);

  const isDepressionTest =
    !!assessmentId &&
    convertToLowerCase(assessmentId) ===
      convertToLowerCase(DEPRESSION_TEST_ASSESSMENT_ID);

  const isBipolarTest =
    !!assessmentId &&
    convertToLowerCase(assessmentId) ===
      convertToLowerCase(BIPOLAR_DISORDER_ASSESSMENT_ID);

  const isPsychopathTest =
    !!assessmentId &&
    convertToLowerCase(assessmentId) ===
      convertToLowerCase(PSYCHOPATH_ASSESSMENT_ID);

  const isChildhood_Trauma =
    !!assessmentId &&
    convertToLowerCase(assessmentId) ===
      convertToLowerCase(CHILDHOOD_TRAUMA_TEST_ASSESSMENT_ID);

  const isSocialAnxietyTest =
    !!assessmentId &&
    convertToLowerCase(assessmentId) ===
      convertToLowerCase(SOCIAL_ANXIETY_TEST_ASSESMENT_ID);

  const isAutismChildrenTest =
    !!assessmentId &&
    convertToLowerCase(assessmentId) ===
      convertToLowerCase(AUTISM_CHILDREN_TEST_ASSESSMENT_ID);

  const fetchEnneagramContent = async () => {
    try {
      setIsEnneagramContentLoading(true); // Start loading
      const result = await getInsightfulContentConnect({
        modelType:
          MODEL_TYPE_ENNEAGRAM[currentLocale] ||
          MODEL_TYPE_ENNEAGRAM[LOCALE.EN],
        contentKey: userAssessmentResult?.personality?.title.toLowerCase(),
      });
      return result;
    } catch (error) {
      console.error("Error fetching enneagram content:", error);
    } finally {
      setIsEnneagramContentLoading(false);
    }
  };

  const finalMaxUserCountGuestUser =
    planData &&
    planData[0] &&
    selectedPlan?.id &&
    planData[0].id &&
    convertToLowerCase(selectedPlan.id) === convertToLowerCase(planData[0].id)
      ? FALSE_PLAN_MAX_USER_COUNT
      : selectedPlan?.maxSubUserCount;

  useEffect(() => {
    if (
      ENNEAGRAM_TEST &&
      userAssessmentResult?.personality?.title.toLowerCase()
    ) {
      fetchEnneagramContent();
    }
  }, [ENNEAGRAM_TEST, userAssessmentResult?.personality?.title]);

  useEffect(() => {
    getPlansConnect({
      // planType: parseInt(res?.caseValue) === 1 ? plan1995 : "_default",
      planType: "_default",
      currencyCD,
      countryCD,
      device: "null",
      locale:
        currentLocale === LOCALE.EN ||
        currentLocale === LOCALE.ES ||
        currentLocale === LOCALE.JA ||
        currentLocale === LOCALE.FR ||
        currentLocale === LOCALE.IT
          ? currentLocale
          : LOCALE.EN,
    }).then((result) => {
      setPlanData(result);
      // Set default selected plan to the second plan (index 1)
      if (result && result.length > 1 && !selectedPlan) {
        dispatch(PushSelectedPlanToFunnelStep(result[1]));
      }
      setIsLoading(false);
      // const timeoutId = setTimeout(() => {
      //   setIsLoading(false);
      // }, 800);
    });
  }, [getPlansConnect, dispatch]);

  const accordianData =
    planData &&
    planData?.map((plan) => {
      const {
        id,
        description,
        label,
        unitPrice,
        scheduleTypeCD,
        recurringPrice,
        currencySymbol,
        maxSubUserCount,
      } = plan;

      // Use 3 for first plan, otherwise use original maxSubUserCount
      const finalMaxSubUserCount =
        planData &&
        planData[0] &&
        id &&
        planData[0].id &&
        convertToLowerCase(id) === convertToLowerCase(planData[0].id)
          ? FALSE_PLAN_MAX_USER_COUNT
          : maxSubUserCount;

      const assessmentType = getAssessmentPlanType({
        isCareerPreferenceTest,
        isRomanticCompatibilityTest,
        isColorBlindTest,
        isDepressionTest,
        isBipolarTest,
        isPsychopathTest,
        isChildhood_Trauma,
        isSocialAnxietyTest,
        isAutismChildrenTest,
      });

      const selectedAssessment = ASSESSMENT_PLAN_CONFIG[assessmentType];

      const inviteFeature = {
        title: `${t("plan.invite")} ${finalMaxSubUserCount} ${t(
          "feature.friends_family_access",
        )}`,
        image: "CareerGuidence",
        description: `${t("plan.invite")} ${finalMaxSubUserCount} ${t(
          "plan.feature.friends_family_access",
        )}`,
      };

      return {
        id: id,
        title: label,
        unitPrice: unitPrice,
        recurringPrice: recurringPrice,
        currencySymbol: currencySymbol,
        scheduleTypeCD: scheduleTypeCD,
        maxSubUserCount: finalMaxSubUserCount,
        // price: `${currencySymbol}${unitPrice} /${scheduleTypeCD}`,
        price: `${currencySymbol}${unitPrice}`,
        content: generatePlanFeatures({
          scheduleTypeCD,
          selectedAssessment,
          inviteFeature,
          t,
          assessmentType,
        }),
        policy: description,
      };
    });

  const onAccordionClick = useCallback(
    (selectedPlan) => {
      dispatch(PushSelectedPlanToFunnelStep(selectedPlan));

      // Handle isFalsePlan cookie based on selected plan
      handleFalsePlanCookie(selectedPlan?.id);
    },
    [dispatch, handleFalsePlanCookie],
  );
  useEffect(() => {
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 100);
  }, []);

  const handleContinue = () => {
    // Handle isFalsePlan cookie based on selected plan
    handleFalsePlanCookie(selectedPlan?.id);

    navigate(
      `${checkoutPath.next}?pt=${personalityType}&sku=${
        selectedPlan?.id
      }&pn=${personalityName}&pnes=${personalityNameEnglishCode}&pg=${personalityGroup}&assessmentAttemptId=${
        assessmentAttemptId || attemptId
      }`,
    );
  };

  return (
    <div className="bg-surface-card">
      <Loader isLoading={isLoading || isEnneagramContentLoading} />

      {/* ===== DESKTOP HEADER ===== */}
      <HeaderV2 />

      {/* ===== MAIN CONTENT ===== */}
      <main className="max-w-[1180px] mx-auto px-[clamp(16px,4vw,40px)] pt-[clamp(24px,5vw,56px)] pb-20 ">
        {/* ===== SECTION TITLE ===== */}
        <div className="text-center max-w-[640px] mx-auto mb-2">
          <div className="text-[0.78rem] font-bold tracking-[0.14em] uppercase text-brand-orange-hover">
            {t("plan_v2.report_ready_label")}
          </div>
          <h1 className="font-bold tracking-[-0.03em] leading-[1.08] text-[clamp(1.7rem,4.5vw,2.5rem)] mt-2 mb-0">
            {t("plan_v2.heading")}
          </h1>
          <p className="text-text-muted text-[clamp(1rem,1.6vw,1.1rem)] mt-3 mb-0">
            {t("plan_v2.subtitle")}
          </p>
        </div>

        <div className="flex flex-wrap gap-[clamp(20px,3vw,36px)] mt-[clamp(24px,3vw,40px)] items-start">
          {/* ===== LEFT COLUMN: Report Preview + Perks ===== */}
          <div className="flex-[1_1_340px] min-w-0">
            {/* Report Preview Card */}
            <div className="relative bg-card-preview-gradient rounded-3xl p-[clamp(20px,3vw,30px)] overflow-hidden">
              <div className="absolute top-[-30px] right-[-20px] w-[150px] h-[150px] rounded-full bg-[rgba(255,140,107,0.16)]" />
              <div className="relative">
                <div className="inline-flex items-center gap-[7px] bg-white rounded-full py-[6px] px-[13px] text-[0.78rem] font-bold text-brand-orange-hover shadow-[0_6px_16px_-10px_rgba(43,35,51,0.4)]">
                  🔓 {t("plan_v2.unlock_badge")}
                </div>

                {/* IQ Bell Curve Chart */}
                <div className="mt-4">
                  <IqBellCurvedCard
                    iqScore={userAssessmentResult?.iqScore ?? 100}
                    onUnlock={handleContinue}
                  />
                </div>

                {/* IQ Assessment label + description */}
                <div className="mt-4">
                  <div className="font-bold text-[1.05rem] text-text-dark">
                    {t("plan_v2.card_title")}
                  </div>
                  <p className="text-text-muted text-[0.88rem] mt-[6px] mb-0 leading-[1.55]">
                    {t("plan_v2.card_description")}
                  </p>
                </div>

                <UIButton
                  variant="block"
                  onClick={handleContinue}
                  className="mt-4  opacity-80 shadow-[0_14px_30px_-14px_rgba(255,140,107,0.7)]"
                >
                  {t("plan_v2.unlock_btn")}
                </UIButton>
              </div>
            </div>

            {/* Premium Perks List */}
            <div className="mt-[22px]">
              <div className="font-bold text-[1.05rem]">
                {t("plan_v2.perks_heading")}
              </div>
              <div className="flex flex-col gap-3 mt-[14px]">
                {PERKS.map(({ icon, colorClass, titleKey, descKey }) => {
                  const title =
                    titleKey === null
                      ? `${t("plan_v2.perk_invite_prefix")} ${finalMaxUserCountGuestUser} ${t("plan_v2.perk_invite_suffix")}`
                      : t(titleKey);
                  return (
                    <div
                      key={titleKey ?? "invite"}
                      className="flex items-start gap-[11px]"
                    >
                      <span
                        className={`flex-none w-[26px] h-[26px] rounded-full mt-px flex items-center justify-center text-[0.82rem] ${colorClass}`}
                      >
                        {icon}
                      </span>
                      <div>
                        <span className="font-bold text-[0.96rem]">
                          {title}
                        </span>
                        <span className="text-text-light text-[0.9rem]">
                          {" "}
                          — {t(descKey)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* ===== RIGHT COLUMN: Plan Cards ===== */}
          <div className="flex-[1_1_380px] min-w-0 bg-white border border-[rgba(43,35,51,0.07)] rounded-3xl p-[clamp(20px,3vw,30px)] shadow-[0_18px_44px_-24px_rgba(43,35,51,0.4)]">
            <div className="font-bold text-[1.2rem]">
              {t("plan_v2.choose_plan")}
            </div>

            {/* Plan Cards */}
            <div className="flex flex-col gap-3 mt-4">
              {(accordianData || []).map((p, i) => {
                const isSelected = selectedPlan?.id === p.id;
                return (
                  <div
                    key={p.id}
                    onClick={() => {
                      onAccordionClick(p);
                      handleFalsePlanCookie(p.id);
                    }}
                    onDoubleClick={handleContinue}
                    className={`relative flex items-center gap-[14px] w-full py-4 px-[18px] rounded-2xl cursor-pointer text-left overflow-hidden transition-[border-color,background] duration-150  text-text-dark ${isSelected ? "bg-brand-orange-light border-2 border-brand-orange-hover" : "bg-white border-2 border-[rgba(43,35,51,0.1)]"}`}
                  >
                    <span
                      className={`flex-none w-[22px] h-[22px] rounded-full flex items-center justify-center ${isSelected ? "bg-brand-orange-hover border-2 border-brand-orange-hover" : "bg-white border-2 border-[rgba(43,35,51,0.22)]"}`}
                    >
                      {isSelected && (
                        <span className="w-[10px] h-[10px] rounded-full bg-white" />
                      )}
                    </span>
                    <span className="flex-1 text-left min-w-0">
                      <span className="block font-bold text-[1.02rem]">
                        {p.title}
                      </span>
                      <span className="block text-[0.82rem] text-text-light mt-[2px]">
                        {p.content?.[0]?.description || ""}
                      </span>
                    </span>
                    <span className="text-right flex-none">
                      <span className="font-bold text-[1.25rem]">
                        {p.price}
                      </span>
                      <span className="text-[0.8rem] text-text-light">
                        {p.scheduleTypeCD === "Monthly" ? " /mo" : ""}
                      </span>
                    </span>
                    {i === 1 && (
                      <span className="absolute top-0 right-0 bg-brand-orange text-white font-bold text-[0.62rem] tracking-[0.06em] uppercase py-1 px-3 rounded-[0_15px_0_12px]">
                        {t("plan_v2.most_popular")}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Money-Back Guarantee Badge */}
            <div className="flex items-center gap-3 mt-[18px] bg-status-success-bg rounded-[14px] py-[14px] px-4">
              <img
                src={ShieldCheck}
                alt=""
                width="26"
                height="26"
                className="flex-none"
              />
              <div>
                <div className="font-bold text-[0.92rem] text-status-success-text">
                  {t("plan_v2.guarantee_title")}
                </div>
                <div className="text-[0.82rem] text-status-success-text">
                  {t("plan_v2.guarantee_desc")}
                </div>
              </div>
            </div>

            {/* Continue Button */}
            <UIButton
              id="plan-continue-button"
              variant="block"
              onClick={() => {
                if (selectedPlan) {
                  handleContinue();
                } else {
                  console.error("No plan selected");
                }
              }}
              className="mt-[18px] opacity-80 shadow-[0_14px_30px_-14px_rgba(255,140,107,0.7)]"
            >
              {t("plan_v2.continue_btn")}
            </UIButton>

            {/* Policy Text */}
            <p className="text-[0.72rem] text-text-light leading-[1.5] mt-3 mx-[2px] mb-0">
              {t("plan_v2.policy_text_1")}{" "}
              <a
                href="mailto:support@insightfultraits.com"
                className="text-brand-orange-hover"
              >
                support@insightfultraits.com
              </a>
              {t("plan_v2.policy_text_2")}
            </p>

            {/* Trust Indicators */}
            <div className="flex flex-wrap gap-x-[18px] gap-y-[14px] justify-center mt-[18px] pt-4 border-t border-[rgba(43,35,51,0.08)]">
              <span className="flex items-center gap-[6px] text-[0.78rem] font-bold text-text-muted">
                🔒 {t("plan_v2.ssl_secure")}
              </span>
              <span className="flex items-center gap-[6px] text-[0.78rem] font-bold text-text-muted">
                🛡️ {t("plan_v2.data_encrypted")}
              </span>
              <span className="flex items-center gap-[6px] text-[0.78rem] font-bold text-text-muted">
                ⭐ {t("plan_v2.trusted_members")}
              </span>
            </div>
          </div>
        </div>
      </main>

      <Footer_V2 />
    </div>
  );
};

export default Plan;
